/**
 * The "Subject details" tab — where a curator edits the description and the
 * aliases for a subject. The Guide Viewer's header stays
 * display-only; all editing lives here, in one calm form.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, IconButton, Input } from '@servicecompressionllc/sc-design-system';
import { X } from '@servicecompressionllc/sc-design-system/icons';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { knowledgeApi, type KnowledgeSubject } from './api.js';
import { SourceDocuments } from './SourceDocuments.js';
import { aliasKey } from './aliasKey.js';

/** Mirrors the API's ALIAS_CATALOG_MAX — a sanity ceiling on list size, not a
 *  retrieval constraint. Matching reads the catalog directly and exactly, so
 *  more names only help. (The old 2-alias cap was a folding-era constraint;
 *  folding is gone.) */
const ALIAS_CATALOG_MAX = 50;

export const SubjectDetailsTab = ({
  subject,
  onStartOver,
}: {
  subject: KnowledgeSubject;
  /** Omitted when there's nothing to start over. */
  onStartOver?: () => void;
}) => (
  // One width for everything on this tab: the tabs above run the page width, so
  // a narrower grid and a narrower card below it left three ragged right edges.
  <div className="flex flex-col gap-4">
    {/* Documents sit beside the details, not below them: they're what the guides
        are written FROM, so they belong in view while you read the subject. */}
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <SubjectDetailsForm subject={subject} />
      <SourceDocuments subjectId={subject.id} />
    </div>
    {onStartOver && <StartOver subject={subject} onStartOver={onStartOver} />}
  </div>
);

/**
 * Destructive actions live at the bottom of the settings surface, marked as
 * such — not beside the title where a stray click reaches them.
 */
const StartOver = ({
  subject,
  onStartOver,
}: {
  subject: KnowledgeSubject;
  onStartOver: () => void;
}) => {
  const me = useMe();
  if (me.data && !canWriteKnowledge(me.data.user)) return null;
  return (
    <div className="rounded-lg border border-danger/40 bg-surface-elevated p-4">
      <p className="text-sm font-medium text-ink">Start over</p>
      <p className="mt-0.5 text-xs text-ink-muted">
        Sends every {subject.itemNoun.toLowerCase()} back to draft so none of them are used in
        answers, and clears the run history. The {subject.itemNoun.toLowerCase()}s and your uploaded
        documents are kept.
      </p>
      <Button variant="danger" className="mt-3" onClick={onStartOver}>
        Start this {subject.subjectNoun.toLowerCase()} over
      </Button>
    </div>
  );
};

const SubjectDetailsForm = ({ subject }: { subject: KnowledgeSubject }) => {
  const me = useMe();
  const readOnly = me.data ? !canWriteKnowledge(me.data.user) : true;
  const qc = useQueryClient();
  const [notes, setNotes] = useState(subject.notes ?? '');
  const [promptContext, setPromptContext] = useState(subject.promptContext ?? '');
  const [aliases, setAliases] = useState<string[]>(subject.aliases);
  const [added, setAdded] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Nothing else in the app links a subject to an entity, so without this the
  // alias editor is unreachable for every subject the roster sync didn't touch.
  // The user never sees the word "entity" — they asked for aliases.
  const enableNames = useMutation({
    mutationFn: async () => {
      const entity = await knowledgeApi.createEntity({ name: subject.name });
      await knowledgeApi.updateSubject(subject.id, { entityId: entity.id });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });

  // The tenant's whole vocabulary, for checking a typed alias AS IT IS TYPED
  // (the RegisterEventDrawer pattern) instead of failing on Save. The server's
  // guard stays authoritative; this is UX.
  const entities = useQuery({
    queryKey: ['knowledge', 'entities'],
    queryFn: knowledgeApi.entities,
    enabled: subject.entityId !== null && !readOnly,
  });
  // Subject names too: an alias equal to another subject's canonical name
  // would take over searches meant for it (the alias path outranks the fuzzy
  // scorer), so the API refuses it — warn here the same way.
  const subjects = useQuery({
    queryKey: ['knowledge', 'subjects'],
    queryFn: knowledgeApi.subjects,
    enabled: subject.entityId !== null && !readOnly,
  });
  /** The name that already claims `text`, or null. Excludes this subject's
   *  own entity and the subjects it backs — keeping your own names is not a
   *  conflict. Mirrors the API's assertNamesUnclaimed. */
  const claimedBy = (text: string): string | null => {
    const key = aliasKey(text);
    if (key.length === 0) return null;
    for (const e of entities.data ?? []) {
      if (e.id === subject.entityId) continue;
      if ([e.name, ...e.aliases].some((n) => aliasKey(n) === key)) return e.name;
    }
    for (const s of subjects.data ?? []) {
      if (s.entityId === subject.entityId || s.id === subject.id) continue;
      if (aliasKey(s.name) === key) return s.name;
    }
    return null;
  };
  const addedTrimmed = added.trim();
  const addedClaim = addedTrimmed.length > 0 ? claimedBy(addedTrimmed) : null;
  const addedIsRepeat =
    addedTrimmed.length > 0 &&
    [subject.name, ...aliases].some((a) => aliasKey(a) === aliasKey(addedTrimmed));
  const addBlocked =
    addedTrimmed.length === 0 ||
    addedClaim !== null ||
    addedIsRepeat ||
    aliases.length >= ALIAS_CATALOG_MAX;

  const save = useMutation({
    mutationFn: async () => {
      await knowledgeApi.updateSubject(subject.id, {
        notes: notes.trim().length > 0 ? notes : null,
        promptContext: promptContext.trim().length > 0 ? promptContext : null,
      });
      if (subject.entityId) {
        await knowledgeApi.updateEntity(subject.entityId, {
          aliases: aliases.map((a) => a.trim()).filter((a) => a.length > 0),
        });
      }
    },
    onSuccess: () => {
      setSavedAt(Date.now());
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });

  return (
    <div className="rounded-lg border border-outline bg-surface-elevated p-4">
      <label className="block text-sm font-medium text-ink">
        Description
        <p className="mt-0.5 text-xs font-normal text-ink-muted">
          A short note about this {subject.subjectNoun.toLowerCase()} for your team.
        </p>
        <textarea
          disabled={readOnly}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setSavedAt(null);
          }}
          rows={3}
          className="mt-1 w-full rounded border border-outline-strong bg-surface p-2 text-sm text-ink"
        />
      </label>

      <label className="mt-4 block text-sm font-medium text-ink">
        What the AI should know about this {subject.subjectNoun.toLowerCase()}
        <p className="mt-0.5 text-xs font-normal text-ink-muted">
          Quirks, known issues, or wording specific to this one — added to the category briefing
          whenever its guides are written. Leave empty if nothing special applies.
        </p>
        <textarea
          disabled={readOnly}
          value={promptContext}
          onChange={(e) => {
            setPromptContext(e.target.value);
            setSavedAt(null);
          }}
          rows={4}
          className="mt-1 w-full rounded border border-outline-strong bg-surface p-2 text-sm text-ink"
        />
      </label>

      <div className="mt-4">
        <p className="text-sm font-medium text-ink">Aliases</p>
        <p className="mt-0.5 text-xs text-ink-muted">
          Every name your team actually uses for this — short codes, older model numbers, nicknames,
          even misspellings you see a lot. The assistant finds these guides when someone types any
          of them. Matching forgives case and punctuation but not variants, so add singular and
          plural forms separately.
        </p>
        {subject.entityId === null ? (
          <div className="mt-2">
            {readOnly ? (
              <p className="text-sm text-ink-subtle">No aliases set up.</p>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => enableNames.mutate()}
                  disabled={enableNames.isPending}
                >
                  {enableNames.isPending ? 'Setting up…' : 'Set up aliases'}
                </Button>
                {enableNames.error && (
                  <p className="mt-2 text-sm text-danger">{(enableNames.error as Error).message}</p>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <ul className="mt-2 space-y-2">
              {aliases.map((a, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <Input
                    value={a}
                    disabled={readOnly}
                    aria-label={`Alias ${String(idx + 1)}`}
                    onChange={(e) => {
                      setAliases(aliases.map((r, i) => (i === idx ? e.target.value : r)));
                      setSavedAt(null);
                    }}
                    className="w-64"
                  />
                  {!readOnly && (
                    <IconButton
                      aria-label={`Remove alias ${String(idx + 1)}`}
                      variant="ghost"
                      onClick={() => {
                        setAliases(aliases.filter((_, i) => i !== idx));
                        setSavedAt(null);
                      }}
                    >
                      <X size={14} />
                    </IconButton>
                  )}
                </li>
              ))}
            </ul>
            {!readOnly && (
              <form
                className="mt-2 flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!addBlocked) {
                    setAliases([...aliases, addedTrimmed]);
                    setAdded('');
                    setSavedAt(null);
                  }
                }}
              >
                <Input
                  value={added}
                  onChange={(e) => setAdded(e.target.value)}
                  placeholder="Add an alias…"
                  aria-label="Add an alias"
                  className="w-64"
                />
                <Button type="submit" variant="secondary" disabled={addBlocked}>
                  Add
                </Button>
              </form>
            )}
            {addedClaim !== null && (
              <p className="mt-2 text-sm text-danger">
                “{addedTrimmed}” is already a name for {addedClaim}. One name can only mean one
                thing — remove it there first, or pick a different name.
              </p>
            )}
            {addedClaim === null && addedIsRepeat && (
              <p className="mt-2 text-xs text-ink-muted">Already a name for this one.</p>
            )}
            {aliases.length >= ALIAS_CATALOG_MAX && (
              <p className="mt-2 text-xs text-ink-muted">
                That’s the limit. Remove one to add another.
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        {!readOnly && (
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save details'}
          </Button>
        )}
        {savedAt !== null && !save.isPending && (
          <span className="text-sm text-success">Saved.</span>
        )}
        {save.error && <span className="text-sm text-danger">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
};
